import { loadStripe } from "@stripe/stripe-js";
import React, { useState } from "react";
import { LineWave } from "react-loader-spinner";
import api from "src/rest/api";
// Import the MODERN version of TrainerPlans
import { BiX } from "react-icons/bi";
import { useNavigate } from "react-router-dom";
import TrainerPlansModern from "./TrainerPlans/modern"; // Ensure the path and export are correct

const PlansOver: React.FC = () => {
  // State for loading indicator during checkout process
  const [loading, setLoading] = useState(false);
  // State to control the billing interval (monthly/yearly)
  const [interval, setInterval] = useState<"month" | "year">("month");

  const goBack = useNavigate();

  // Function to handle the checkout process when a plan is selected
  async function handleCheckoutRequest(priceId: string) {
    // Check if a valid priceId is provided
    if (!priceId) {
      alert("ID do preço inválido ou não fornecido.");
      return;
    }
    try {
      setLoading(true);
      // Fetch Stripe publishable key from backend
      const key = await api.post<{ publishableKey: string }>("/stripe/config");
      const stripe = await loadStripe(key.data.publishableKey);

      if (!stripe) {
        throw new Error("Falha ao carregar o Stripe.");
      }

      // Create a Stripe Checkout session with the selected price ID
      const session = await api.post<{ session: { id: string } }>(
        "/stripe/session-payment",
        { priceId: priceId }, // Send priceId instead of plan object/id
      );

      // Redirect user to Stripe Checkout page
      const result = await stripe.redirectToCheckout({
        sessionId: session.data.session.id,
      });

      // Handle potential errors during redirection
      if (result.error) {
        throw new Error(
          result.error.message || "Erro ao redirecionar para o checkout.",
        );
      }
    } catch (err: any) {
      console.error("Erro no checkout:", err);
      // Provide more specific error feedback if possible
      alert(
        `Erro ao iniciar o checkout: ${err.message || "Tente novamente mais tarde."}`,
      );
    } finally {
      setLoading(false);
    }
  }

  // Display loading overlay if checkout is in progress
  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-black/80 to-black">
        <LineWave color="white" />
      </div>
    );
  }

  // Main component layout
  return (
    <div className="fade-in fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-zinc-900/50 p-4 backdrop-blur-sm">
      <div className="mx-auto w-full max-w-5xl rounded-lg bg-zinc-800/70 p-6 shadow-xl sm:p-8">
        <div className="relative mx-auto flex flex-col items-center">
          <button
            onClick={() => goBack(-1)}
            className="absolute left-0 top-0 mb-4 flex cursor-pointer items-center gap-2 rounded-full bg-black/50 text-zinc-100 hover:text-white"
          >
            <BiX size={24} />
          </button>
          <h2 className="mb-6 text-center text-3xl font-bold text-zinc-100">
            Escolha seu Plano
          </h2>

          {/* Interval Toggle */}
          <div className="mb-8 flex justify-center">
            <div
              className="inline-flex rounded-md bg-zinc-700 p-1 shadow-sm"
              role="group"
            >
              <button
                type="button"
                onClick={() => setInterval("month")}
                className={`rounded-l-md px-6 py-2 text-sm font-medium transition-colors duration-200 ease-in-out ${interval === "month" ? "bg-zinc-600 text-white shadow-md" : "text-zinc-100 hover:bg-zinc-600"}`}
              >
                Mensal
              </button>
              <button
                type="button"
                onClick={() => setInterval("year")}
                className={`relative rounded-r-md px-6 py-2 text-sm font-medium transition-colors duration-200 ease-in-out ${interval === "year" ? "bg-zinc-600 text-white shadow-md" : "text-zinc-100 hover:bg-zinc-600"}`}
              >
                Anual
                <span className="absolute -right-2 -top-3 rounded-full bg-primary-800 px-2 py-0.5 pb-1 text-xs font-bold text-white">
                  Economize!
                </span>
              </button>
            </div>
          </div>

          <TrainerPlansModern
            interval={interval}
            onCheckoutRequest={handleCheckoutRequest}
          />
        </div>
      </div>
    </div>
  );
};

export default PlansOver;
